import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus, Trash2, CheckCircle, Circle, ChevronDown } from 'lucide-react';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
}

interface AssignUsersModalProps {
    roleName: string;
    assignedUsers: User[];
    availableUsers: User[]; // All system users
    onClose: () => void;
    onAssign: (userId: string) => void;
    onRemove: (userId: string) => void;
}

const AssignUsersModal: React.FC<AssignUsersModalProps> = ({
    roleName,
    assignedUsers,
    availableUsers,
    onClose,
    onAssign,
    onRemove
}) => {
    const [selectedUserId, setSelectedUserId] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const unassignedUsers = availableUsers.filter(
        (u) => !assignedUsers.find((au) => au.id === au.id)
    );

    const filteredUsers = unassignedUsers.filter((user) =>
        `${user.firstName} ${user.lastName} ${user.email}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );
    const getFullName = (u: User) => `${u.firstName} ${u.lastName}`;

    return createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-6 font-sans">
            <div className="bg-neutral-900 text-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-neutral-800 transition-all duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-neutral-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-900/30 flex items-center justify-center">
                            <UserPlus className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold leading-tight">Assign Users</h3>
                            <p className="text-xs text-gray-400 font-medium">Role: <span className="text-indigo-400">{roleName}</span></p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-all"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    {/* User Selection */}
                    <div className="flex gap-3 items-center mb-8">
                        <div className="relative flex-1">
                            <div
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="bg-neutral-800 text-white text-sm rounded-xl px-4 py-3 flex items-center justify-between cursor-pointer border border-neutral-700 hover:border-indigo-400 transition-all"
                            >
                                <span className="font-medium text-gray-300">
                                    {selectedUserId
                                        ? getFullName(unassignedUsers.find((u) => u.id === selectedUserId)!) +
                                        ' - ' +
                                        unassignedUsers.find((u) => u.id === selectedUserId)!.email
                                        : 'Select a user to assign...'}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-400 ml-2" />
                            </div>

                            {dropdownOpen && (
                                <div className="absolute z-50 mt-2 w-full max-h-60 overflow-y-auto bg-neutral-900 border border-neutral-700 rounded-xl shadow-2xl">
                                    <div className="sticky top-0 p-2 bg-neutral-900 border-b border-neutral-800">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search by name or email..."
                                            className="w-full bg-neutral-800 text-white px-3 py-2 text-sm rounded-lg outline-none ring-1 ring-transparent focus:ring-indigo-500 placeholder:text-gray-400 transition-all"
                                        />
                                    </div>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => (
                                            <div
                                                key={user.id}
                                                onClick={() => {
                                                    setSelectedUserId(user.id);
                                                    setDropdownOpen(false);
                                                    setSearchTerm('');
                                                }}
                                                className="px-4 py-2.5 hover:bg-neutral-800 text-gray-200 text-sm cursor-pointer transition-colors"
                                            >
                                                <div className="font-bold text-white">{getFullName(user)}</div>
                                                <div className="text-[10px] text-gray-400">{user.email}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-3 text-gray-400 text-xs text-center italic">No matching users found.</div>
                                    )}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => {
                                if (selectedUserId) onAssign(selectedUserId);
                                setSelectedUserId('');
                            }}
                            disabled={!selectedUserId}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-bold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all disabled:opacity-50"
                        >
                            Add User
                        </button>
                    </div>

                    {/* Assigned Users Table */}
                    <div className="overflow-hidden border border-neutral-800 rounded-2xl bg-black/10">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-neutral-950 text-gray-400 uppercase text-[10px] font-black tracking-widest">
                                <tr>
                                    <th className="px-4 py-3">Full Name</th>
                                    <th className="px-4 py-3">Email</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-800">
                                {assignedUsers.map(user => (
                                    <tr key={user.id} className="hover:bg-neutral-800 transition">
                                        <td className="px-4 py-3 text-white font-medium">{getFullName(user)}</td>
                                        <td className="px-4 py-3 text-white">{user.email}</td>
                                        <td className="px-4 py-3">
                                            {user.isActive ? (
                                                <span className="flex items-center gap-1.5 text-green-400 font-bold text-[11px]">
                                                    <CheckCircle className="w-3.5 h-3.5" /> Active
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5 text-gray-400 font-bold text-[11px]">
                                                    <Circle className="w-3.5 h-3.5" /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setConfirmDeleteId(user.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-all"
                                                title="Unassign"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {assignedUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-center py-10 text-gray-400 italic text-xs">
                                            No users are currently assigned to this role.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 rounded-xl border border-neutral-700 text-gray-300 font-bold text-sm hover:bg-neutral-800 transition-all"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>

                {/* Confirm Dialog */}
                {confirmDeleteId && (
                    <ConfirmDialog
                        message="Are you sure you want to unassign this user from the role?"
                        onYes={() => {
                            onRemove(confirmDeleteId);
                            setConfirmDeleteId(null);
                        }}
                        onCancel={() => setConfirmDeleteId(null)}
                    />
                )}
            </div>
        </div>,
        document.body
    );
};

export default AssignUsersModal;
